import { css } from "@emotion/react";
import type {
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Text } from "@/ui/atoms/text";
import { CtaGroup } from "@/ui/molecules/cta_group";
import { AllFeaturesTables } from "@/ui/organisms/all_features_tables";
import { LegacyTweetGrid } from "@/ui/organisms/legacy_tweet_grid";

import { generatePageLevelProps } from "@/util/data_util";
import { generateLegacyPreFooterData } from "@/util/strapi_data_util";
import { getAllFeaturesPage, getGlobals } from "@/util/strapi_util";

export const getStaticProps = async ({
    preview: previewMode = false,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = "all-features";

    const globalsData = await getGlobals(previewMode);
    const pageData = await getAllFeaturesPage(previewMode);

    return {
        props: {
            ...generatePageLevelProps({ path: `/${slug}`, previewMode, slug }),
            ...generateLegacyPreFooterData(globalsData, pageData.Pre_Footer),
            globalsData,
            pageData,
        },
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    return (
        <>
            <GridContainer
                className={css({
                    paddingBottom: Spacing["spacing-18"],
                    paddingTop: Spacing["spacing-18"],
                })}
            >
                <GridColumn columnSpan="full" columnStart={1}>
                    <Text
                        fontSize="HeadlineDefault"
                        tag="h1"
                        textAlign="center"
                    >
                        {props.pageData.Headline}
                    </Text>

                    <Text
                        className={css({
                            marginBottom: Spacing["spacing-12"],
                            marginTop: Spacing["spacing-4"],
                        })}
                        shouldBalanceWrap={true}
                        textAlign="center"
                        themeKey="textPrimary"
                    >
                        {props.pageData.Description}
                    </Text>
                </GridColumn>

                {props.pageData.CTA_Group && (
                    <GridColumn
                        columnSpan={{
                            extraSmall: "full",
                            medium: 6,
                        }}
                        columnStart={{
                            extraSmall: 1,
                            medium: 4,
                        }}
                    >
                        <CtaGroup
                            {...props.pageData.CTA_Group}
                            alignment="center"
                        />
                    </GridColumn>
                )}
            </GridContainer>

            <AllFeaturesTables
                allFeaturesTables={props.pageData.Feature_Tables}
            />

            {props.pageData.Tweet_Grid && (
                <>
                    <Divider
                        marginBottom="DefaultSectionMargin"
                        marginTop="DefaultSectionMargin"
                    />

                    <LegacyTweetGrid {...props.pageData.Tweet_Grid} />
                </>
            )}
        </>
    );
};

export default Page;
